import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useNavBadges } from '../../hooks/useNavBadges';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user, isDemoMode } = useAuth();
  const { notifications, unreadCount, loading: notifLoading } = useNotifications(user);
  const { projectsCount, permitsCount } = useNavBadges(user);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef?.current && !notifRef?.current?.contains(e?.target)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', badge: null },
    { label: 'Customers', path: '/customers', icon: 'Users', badge: null },
    { label: 'Projects', path: '/projects', icon: 'Briefcase', badge: projectsCount },
    { label: 'Contractors', path: '/contractors', icon: 'HardHat', badge: null },
    { label: 'Permits & Compliance', path: '/permits', icon: 'FileCheck', badge: permitsCount },
    { label: 'Calendar', path: '/calendar', icon: 'Calendar', badge: null },
    { label: 'Team Members', path: '/team-members', icon: 'UserCog', badge: null },
    { label: 'Reports', path: '/reports-analytics', icon: 'BarChart3', badge: null },
    ...(!isDemoMode ? [{ label: 'Settings', path: '/settings', icon: 'Settings', badge: null }] : [])
  ];

  // Bottom nav shows 5 most important items
  const bottomNavItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', badge: null },
    { label: 'Projects', path: '/projects', icon: 'Briefcase', badge: projectsCount },
    { label: 'Permits', path: '/permits', icon: 'FileCheck', badge: permitsCount },
    { label: 'Customers', path: '/customers', icon: 'Users', badge: null },
    { label: 'More', path: null, icon: 'Menu', badge: null, isMenu: true },
  ];

  const isActive = (path) => location?.pathname === path;

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUrgencyColor = (urgency) => {
    if (urgency === 'high') return 'text-destructive';
    if (urgency === 'medium') return 'text-warning';
    return 'text-muted-foreground';
  };

  const getUrgencyDot = (urgency) => {
    if (urgency === 'high') return 'bg-destructive';
    if (urgency === 'medium') return 'bg-warning';
    return 'bg-muted-foreground';
  };

  const formatNotifDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      {/* ── Desktop/Tablet Top Header ── */}
      <header className="header-nav">
        <div className="header-nav-container">
          <Link to="/dashboard" className="header-logo">
            <img
              src="/assets/images/adu_flow_logo.png"
              alt="ADU Flow logo - ADU Construction CRM"
              className="h-12 w-auto object-contain"
            />
          </Link>

          <nav className="header-nav-menu">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`header-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
                {item?.badge && item?.badge > 0 && (
                  <span className="header-nav-badge">{item?.badge}</span>
                )}
              </Link>
            ))}
          </nav>

          <div className="header-nav-actions">
            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                className="relative w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-250 focus-ring"
                aria-label="Notifications"
                onClick={() => setIsNotifOpen(prev => !prev)}
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {isNotifOpen && (
                <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h3 className="font-semibold text-foreground text-sm">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="text-xs text-muted-foreground">{unreadCount} upcoming</span>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {notifLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                      </div>
                    ) : notifications?.length === 0 ? (
                      <div className="text-center py-8 px-4">
                        <Icon name="CheckCircle" size={32} className="mx-auto mb-2 text-success opacity-60" />
                        <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
                      </div>
                    ) : (
                      notifications?.map((notif) => (
                        <button
                          key={notif?.id}
                          onClick={() => {
                            setIsNotifOpen(false);
                            navigate(notif?.path || '/dashboard');
                          }}
                          className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left border-b border-border/50 last:border-0"
                        >
                          <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${getUrgencyDot(notif?.urgency)}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold truncate ${getUrgencyColor(notif?.urgency)}`}>
                              {notif?.title}
                            </p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {notif?.message}
                            </p>
                            {notif?.date && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {formatNotifDate(notif?.date)}
                              </p>
                            )}
                          </div>
                          <Icon name={notif?.icon || 'Bell'} size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                        </button>
                      ))
                    )}
                  </div>

                  <div className="px-4 py-2 border-t border-border">
                    <button
                      onClick={() => { setIsNotifOpen(false); navigate('/calendar'); }}
                      className="text-xs text-primary hover:underline"
                    >
                      View Calendar →
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-250 focus-ring"
              aria-label="User menu"
            >
              <Icon name="User" size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-destructive/10 text-destructive transition-colors duration-250 focus-ring"
              aria-label="Logout"
              title="Logout"
            >
              <Icon name="LogOut" size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile Slide-out Menu (full screen) ── */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[1010] bg-black/50 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}
      <div className={`header-mobile-menu ${!isMobileMenuOpen ? 'closed' : ''}`}>
        <div className="header-mobile-menu-content">
          {/* Header inside menu */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
            <Link to="/dashboard" className="header-logo" onClick={closeMobileMenu}>
              <img
                src="/assets/images/adu_flow_logo.png"
                alt="ADU Flow"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <button
              onClick={closeMobileMenu}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
              aria-label="Close menu"
            >
              <Icon name="X" size={22} />
            </button>
          </div>

          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`header-mobile-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <Icon name={item?.icon} size={22} />
              <span className="flex-1">{item?.label}</span>
              {item?.badge && item?.badge > 0 && (
                <span className="header-nav-badge">{item?.badge}</span>
              )}
            </Link>
          ))}

          {/* Notifications in menu */}
          <div className="mt-4 pt-4 border-t border-border">
            <button
              onClick={() => { closeMobileMenu(); navigate('/calendar'); }}
              className="header-mobile-nav-item w-full"
            >
              <div className="relative">
                <Icon name="Bell" size={22} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-destructive text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="flex-1">Notifications</span>
              {unreadCount > 0 && (
                <span className="text-xs text-muted-foreground">{unreadCount} new</span>
              )}
            </button>
          </div>

          <button
            onClick={handleLogout}
            className="header-mobile-nav-item text-destructive hover:bg-destructive/10 mt-2"
          >
            <Icon name="LogOut" size={22} />
            <span className="flex-1">Logout</span>
          </button>
        </div>
      </div>

      {/* ── Mobile Bottom Navigation Bar ── */}
      <nav className="mobile-bottom-nav lg:hidden" aria-label="Mobile navigation">
        {bottomNavItems?.map((item) => {
          if (item?.isMenu) {
            return (
              <button
                key="more"
                onClick={toggleMobileMenu}
                className={`mobile-bottom-nav-item ${isMobileMenuOpen ? 'active' : ''}`}
                aria-label="More navigation options"
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={22} />
                <span>More</span>
              </button>
            );
          }
          return (
            <Link
              key={item?.path}
              to={item?.path}
              className={`mobile-bottom-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              aria-label={item?.label}
            >
              <div className="relative">
                <Icon name={item?.icon} size={22} />
                {item?.badge && item?.badge > 0 && (
                  <span className="mobile-bottom-nav-badge">
                    {item?.badge > 9 ? '9+' : item?.badge}
                  </span>
                )}
              </div>
              <span>{item?.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default Header;